import React from 'react';

export interface CreateContextProps {
  /**
   * The context name
   */
  name?: string;

  /**
   * If `true`, React will throw if context is `null` or `undefined`
   */
  strict?: boolean;
}

export function createContext<ContextType>(props: CreateContextProps = {}) {
  const { name, strict = true } = props;

  const Context = React.createContext<ContextType | undefined>(undefined);

  Context.displayName = name;

  function useContext() {
    const context = React.useContext(Context);

    if (!context && strict) {
      const error = new Error(
        `useContext(${name}): context is undefined. Seems you forgot to wrap component within the Provider`
      );

      error.name = 'ContextError';
      Error.captureStackTrace?.(error, useContext);

      throw error;
    }

    return context;
  }

  const Provider = Context.Provider;

  return {
    Provider,
    useContext,
    Context,
  };
}

export default createContext;
